import { motion } from 'framer-motion';
import { X, Sun, Minus, Plus, Type, AlignJustify, Columns } from 'lucide-react';
import type { ReaderSettings } from '../types';

interface ReadingPreferencesProps {
  settings: ReaderSettings;
  onSettingsChange: (settings: ReaderSettings) => void;
  onClose: () => void;
}

export default function ReadingPreferences({
  settings,
  onSettingsChange,
  onClose,
}: ReadingPreferencesProps) {
  const updateSetting = <K extends keyof ReaderSettings>(
    key: K,
    value: ReaderSettings[K]
  ) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const isDark = settings.theme === 'dark';

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 100,
        }}
      />

      {/* Panel */}
      <motion.div
        initial={{ x: 320 }}
        animate={{ x: 0 }}
        exit={{ x: 320 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '320px',
          maxWidth: '85vw',
          background: isDark ? '#1a1a2e' : '#ffffff',
          zIndex: 101,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontWeight: 600,
              color: isDark ? '#fff' : 'var(--text)',
              fontSize: '1rem',
            }}
          >
            Reading Preferences
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '0.5rem',
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-light)',
              display: 'flex',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Settings */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
          {/* Theme */}
          <SettingSection title="Theme" icon={<Sun size={18} />} isDark={isDark}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <ThemeButton
                active={settings.theme === 'light'}
                onClick={() => updateSetting('theme', 'light')}
                label="Light"
                preview={{ bg: '#ffffff', text: '#1e293b' }}
              />
              <ThemeButton
                active={settings.theme === 'dark'}
                onClick={() => updateSetting('theme', 'dark')}
                label="Dark"
                preview={{ bg: '#1a1a2e', text: '#e4e4e7' }}
              />
              <ThemeButton
                active={settings.theme === 'sepia'}
                onClick={() => updateSetting('theme', 'sepia')}
                label="Sepia"
                preview={{ bg: '#f4ecd8', text: '#5c4b37' }}
              />
            </div>
          </SettingSection>

          {/* Font Size */}
          <SettingSection title="Font Size" icon={<Type size={18} />} isDark={isDark}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
              }}
            >
              <button
                onClick={() => updateSetting('fontSize', Math.max(14, settings.fontSize - 2))}
                disabled={settings.fontSize <= 14}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'var(--light-gray)'}`,
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--off-white)',
                  color: isDark ? '#fff' : 'var(--text)',
                  cursor: settings.fontSize <= 14 ? 'not-allowed' : 'pointer',
                  opacity: settings.fontSize <= 14 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Minus size={18} />
              </button>

              <div style={{ flex: 1, textAlign: 'center' }}>
                <span
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: isDark ? '#fff' : 'var(--text)',
                    fontFamily: 'var(--font-serif)',
                  }}
                >
                  {settings.fontSize}
                </span>
                <span
                  style={{
                    fontSize: '0.875rem',
                    color: isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-light)',
                    marginLeft: '0.25rem',
                  }}
                >
                  px
                </span>
              </div>

              <button
                onClick={() => updateSetting('fontSize', Math.min(28, settings.fontSize + 2))}
                disabled={settings.fontSize >= 28}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'var(--light-gray)'}`,
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--off-white)',
                  color: isDark ? '#fff' : 'var(--text)',
                  cursor: settings.fontSize >= 28 ? 'not-allowed' : 'pointer',
                  opacity: settings.fontSize >= 28 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Plus size={18} />
              </button>
            </div>
          </SettingSection>

          {/* Line Height */}
          <SettingSection title="Line Spacing" icon={<AlignJustify size={18} />} isDark={isDark}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[
                { value: 1.5, label: 'Compact' },
                { value: 1.8, label: 'Normal' },
                { value: 2.2, label: 'Relaxed' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateSetting('lineHeight', option.value)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border:
                      settings.lineHeight === option.value
                        ? '2px solid var(--accent)'
                        : `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'var(--light-gray)'}`,
                    background:
                      settings.lineHeight === option.value
                        ? isDark
                          ? 'rgba(220, 38, 38, 0.15)'
                          : 'var(--accent-light)'
                        : isDark
                        ? 'rgba(255,255,255,0.05)'
                        : 'var(--off-white)',
                    color:
                      settings.lineHeight === option.value
                        ? 'var(--accent)'
                        : isDark
                        ? '#fff'
                        : 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    fontWeight: settings.lineHeight === option.value ? 600 : 500,
                    transition: 'all 0.2s',
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </SettingSection>

          {/* Column Width */}
          <SettingSection title="Column Width" icon={<Columns size={18} />} isDark={isDark}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[
                { value: 'narrow' as const, label: 'Narrow', width: '580px' },
                { value: 'medium' as const, label: 'Medium', width: '720px' },
                { value: 'wide' as const, label: 'Wide', width: '900px' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateSetting('columnWidth', option.value)}
                  style={{
                    flex: 1,
                    padding: '0.75rem 0.5rem',
                    borderRadius: '0.5rem',
                    border:
                      settings.columnWidth === option.value
                        ? '2px solid var(--accent)'
                        : `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'var(--light-gray)'}`,
                    background:
                      settings.columnWidth === option.value
                        ? isDark
                          ? 'rgba(220, 38, 38, 0.15)'
                          : 'var(--accent-light)'
                        : isDark
                        ? 'rgba(255,255,255,0.05)'
                        : 'var(--off-white)',
                    color:
                      settings.columnWidth === option.value
                        ? 'var(--accent)'
                        : isDark
                        ? '#fff'
                        : 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    fontWeight: settings.columnWidth === option.value ? 600 : 500,
                    transition: 'all 0.2s',
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </SettingSection>

          {/* Preview */}
          <div
            style={{
              marginTop: '2rem',
              padding: '1.5rem',
              borderRadius: '0.75rem',
              background:
                settings.theme === 'dark'
                  ? '#1a1a2e'
                  : settings.theme === 'sepia'
                  ? '#f4ecd8'
                  : '#ffffff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
            }}
          >
            <p
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--accent)',
                marginBottom: '0.75rem',
                fontWeight: 600,
              }}
            >
              Preview
            </p>
            <p
              style={{
                fontSize: `${settings.fontSize}px`,
                lineHeight: settings.lineHeight,
                color:
                  settings.theme === 'dark'
                    ? '#e4e4e7'
                    : settings.theme === 'sepia'
                    ? '#5c4b37'
                    : 'var(--text)',
                margin: 0,
              }}
            >
              The quick brown fox jumps over the lazy dog. This sample text shows how your reading
              experience will look with current settings.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
            background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--off-white)',
          }}
        >
          <button
            onClick={() =>
              onSettingsChange({
                fontSize: 18,
                lineHeight: 1.8,
                theme: 'light',
                columnWidth: 'medium',
              })
            }
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'none',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'var(--light-gray)'}`,
              borderRadius: '0.5rem',
              color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-light)',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Reset to Defaults
          </button>
        </div>
      </motion.div>
    </>
  );
}

function SettingSection({
  title,
  icon,
  children,
  isDark,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div style={{ marginBottom: '2rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
          color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-light)',
        }}
      >
        {icon}
        <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function ThemeButton({
  active,
  onClick,
  label,
  preview,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  preview: { bg: string; text: string };
}) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '0.75rem',
        borderRadius: '0.75rem',
        border: active ? '2px solid var(--accent)' : '1px solid var(--light-gray)',
        background: preview.bg,
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <div
        style={{
          width: '100%',
          padding: '0.5rem',
          borderRadius: '0.375rem',
          background: preview.bg,
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            height: '4px',
            background: preview.text,
            borderRadius: '2px',
            width: '80%',
            marginBottom: '4px',
          }}
        />
        <div
          style={{
            height: '3px',
            background: preview.text,
            borderRadius: '2px',
            width: '60%',
            opacity: 0.5,
          }}
        />
      </div>
      <span
        style={{
          fontSize: '0.75rem',
          fontWeight: active ? 600 : 500,
          color: active ? 'var(--accent)' : preview.text,
        }}
      >
        {label}
      </span>
    </button>
  );
}
